import { Box, LinearProgress, Typography } from "@mui/material";
import { normalizeNikolaActivity, type NikolaActivity, type NikolaMessagePart } from "@chessla/shared";

type ActivityCopy = { running: string; complete: string; detail: string };
const DEFAULT_COPY: ActivityCopy = {
  running: "BUILDING YOUR ANSWER",
  complete: "ANSWER BUILT",
  detail: "Connecting the useful evidence into one coaching reply.",
};
const COPY: Readonly<Record<string, ActivityCopy>> = {
  coach_thinking: DEFAULT_COPY,
  player_data_checked: {
    running: "COUNTING YOUR CHESS DATA",
    complete: "CHESS DATA COUNTED",
    detail: "Checking the full analyzed scope, not only recent examples.",
  },
  games_reviewed: {
    running: "LOOKING THROUGH YOUR GAMES",
    complete: "GAMES CHECKED",
    detail: "Using only games inside the context you chose.",
  },
  position_checked: {
    running: "CHECKING THE POSITION",
    complete: "POSITION CHECKED",
    detail: "Verifying the position before explaining it.",
  },
  line_compared: {
    running: "COMPARING THE MOVES",
    complete: "CANDIDATES COMPARED",
    detail: "Following only the line needed to establish the consequence.",
  },
  patterns_checked: {
    running: "COMPARING YOUR PATTERNS",
    complete: "PATTERNS CHECKED",
    detail: "Keeping each time format separate while checking recurrence.",
  },
  development_checked: {
    running: "CHECKING YOUR DEVELOPMENT",
    complete: "DEVELOPMENT CHECKED",
    detail: "Checking your verified ratings, results, and opening tendencies.",
  },
  guide_checked: {
    running: "READING THIS GAME",
    complete: "GUIDE CHECKED",
    detail: "Checking the exact workspace instructions before answering.",
  },
  analysis_started: {
    running: "CALCULATING THE LINE",
    complete: "GAME ANALYZED",
    detail: "The analysis is running locally in this browser.",
  },
  memory_searched: {
    running: "CHECKING YOUR COACHING HISTORY",
    complete: "COACHING HISTORY CHECKED",
    detail: "Looking for relevant context from your earlier Nikola conversations.",
  },
  web_searched: {
    running: "RESEARCHING TRUSTED SOURCES",
    complete: "WEB RESEARCH CHECKED",
    detail: "Finding current chess information outside your private game data.",
  },
  source_read: {
    running: "READING THE SOURCE",
    complete: "SOURCE READ",
    detail: "Checking the source before using it in your answer.",
  },
  sources_cross_checked: {
    running: "CROSS-CHECKING SOURCES",
    complete: "SOURCES CROSS-CHECKED",
    detail: "Comparing independent sources before drawing a conclusion.",
  },
};

type ActivityGlyph = { glyph: string; token: string };
const DEFAULT_ICON: ActivityGlyph = { glyph: "···", token: "compose" };
const ICONS: Readonly<Record<string, ActivityGlyph>> = {
  coach_thinking: DEFAULT_ICON,
  player_data_checked: { glyph: "Σ", token: "player-data" },
  games_reviewed: { glyph: "▤", token: "games" },
  position_checked: { glyph: "⌖", token: "position" },
  line_compared: { glyph: "⇄", token: "lines" },
  patterns_checked: { glyph: "⌁", token: "patterns" },
  development_checked: { glyph: "↗", token: "development" },
  guide_checked: { glyph: "?", token: "guide" },
  analysis_started: { glyph: "⚙", token: "analysis" },
  memory_searched: { glyph: "◉", token: "memory-search" },
  web_searched: { glyph: "⌕", token: "web-search" },
  source_read: { glyph: "◫", token: "source-read" },
  sources_cross_checked: { glyph: "⇄", token: "source-cross-check" },
};

const TOOL_ICONS: Readonly<Record<string, ActivityGlyph>> = {
  sync_games: { glyph: "⇣", token: "sync-games" },
  recent_games: { glyph: "▤", token: "recent-games" },
  start_analysis: { glyph: "⚙", token: "start-analysis" },
  refresh_analysis: { glyph: "↻", token: "refresh-analysis" },
  game_review: { glyph: "♜", token: "game-review" },
  game_search: { glyph: "⌕", token: "game-search" },
  player_record: { glyph: "Σ", token: "player-record" },
  player_development: { glyph: "↗", token: "player-development" },
  pattern_record: { glyph: "⌁", token: "pattern-record" },
  line_evaluation: { glyph: "∿", token: "line-evaluation" },
  move_evidence: { glyph: "⌖", token: "move-evidence" },
  critical_moments: { glyph: "!", token: "critical-moments" },
  pattern_examples: { glyph: "◇", token: "pattern-examples" },
  candidate_comparison: { glyph: "⇄", token: "candidate-comparison" },
  help_guide: { glyph: "?", token: "help-guide" },
  source_read: { glyph: "◫", token: "source-read" },
  memory_search: { glyph: "◉", token: "memory-search" },
  web_search: { glyph: "⌕", token: "web-search" },
  source_cross_check: { glyph: "⇄", token: "source-cross-check" },
};

type GlyphMotion = "pulse" | "rise" | "scan" | "spin" | "sway";
const glyphMotion = (token: string): GlyphMotion => {
  if (/analysis|refresh/.test(token)) return "spin";
  if (/search|source-read|games/.test(token)) return "scan";
  if (/comparison|lines|cross-check/.test(token)) return "sway";
  if (/development/.test(token)) return "rise";
  return "pulse";
};

export const nikolaActivityLabel = (activity: NikolaActivity): string => {
  if (activity.status === "complete") return "";
  return (
    normalizeNikolaActivity(activity)?.label ??
    (activity.status === "failed"
      ? "EVIDENCE CHECK COULD NOT FINISH"
      : (COPY[String(activity.kind)] ?? DEFAULT_COPY).running)
  );
};

export const currentNikolaActivityLabel = (messages: readonly { parts?: readonly NikolaMessagePart[] }[]): string => {
  const parts = messages.at(-1)?.parts ?? [];
  for (let partIndex = parts.length - 1; partIndex >= 0; partIndex -= 1) {
    const part = parts[partIndex];
    if (part?.type === "activity" && part.activity.status === "running") return nikolaActivityLabel(part.activity);
  }
  return "";
};

export const NikolaActivityStatus = ({ label }: { label: string }) => (
  <Box
    component="span"
    role="status"
    aria-live="polite"
    aria-atomic="true"
    sx={{
      position: "absolute",
      width: 1,
      height: 1,
      p: 0,
      m: -1,
      overflow: "hidden",
      clip: "rect(0 0 0 0)",
      whiteSpace: "nowrap",
      border: 0,
    }}
  >
    {label}
  </Box>
);

export const NikolaActivityReceipt = ({ activity }: { activity: NikolaActivity }) => {
  const fallback = COPY[String(activity.kind)] ?? DEFAULT_COPY;
  const safeActivity =
    normalizeNikolaActivity(activity) ??
    ({
      ...activity,
      label: activity.status === "complete" ? fallback.complete : fallback.running,
      detail: fallback.detail,
    } as const);
  const copy = COPY[String(safeActivity.kind)] ?? DEFAULT_COPY;
  const icon =
    (safeActivity.icon ? TOOL_ICONS[String(safeActivity.icon)] : ICONS[String(safeActivity.kind)]) ?? DEFAULT_ICON;
  const motion = glyphMotion(icon.token);
  const failed = safeActivity.status === "failed";
  const progress =
    safeActivity.kind === "analysis_started" &&
    safeActivity.status === "running" &&
    typeof safeActivity.progress === "number" &&
    Number.isFinite(safeActivity.progress)
      ? Math.min(100, Math.max(0, Math.round(safeActivity.progress)))
      : null;
  const complete = safeActivity.status === "complete";
  const runningLabel = safeActivity.label;
  let runningDetail = safeActivity.detail;
  if (runningDetail === "") runningDetail = copy.detail;
  runningDetail ??= copy.detail;
  return (
    <Box
      data-activity-status={safeActivity.status}
      aria-hidden={safeActivity.status === "complete" ? undefined : true}
      sx={{
        display: "grid",
        gridTemplateColumns: complete ? "24px 1fr" : "36px 1fr",
        gap: complete ? 0.75 : 1.25,
        alignItems: "center",
        my: complete ? 0.5 : 1.25,
        p: complete ? 0.65 : 1.25,
        minHeight: complete ? 36 : 60,
        bgcolor: "rgba(240,236,226,.035)",
        border: "1px solid",
        borderColor: failed ? "error.main" : "rgba(240,236,226,.3)",
        borderLeftWidth: 4,
      }}
    >
      <Box
        data-activity-icon={activity.icon ?? activity.kind}
        data-activity-kind={activity.kind}
        data-activity-glyph={failed ? "failed" : icon.token}
        aria-hidden
        sx={{
          display: "grid",
          placeItems: "center",
          width: complete ? 24 : 36,
          height: complete ? 24 : 36,
          bgcolor: failed ? "error.main" : "primary.main",
          color: "background.default",
          fontSize: complete ? ".78rem" : "1.15rem",
          fontWeight: 950,
        }}
      >
        <Box
          component="span"
          data-activity-glyph-element="true"
          data-activity-motion={motion}
          sx={{
            display: "inline-block",
            lineHeight: 1,
            animation: safeActivity.status === "running" ? `nikola-glyph-${motion} 1.15s ease-in-out infinite` : "none",
            "@keyframes nikola-glyph-pulse": {
              "0%, 100%": { opacity: 0.65, transform: "scale(.84)" },
              "50%": { opacity: 1, transform: "scale(1)" },
            },
            "@keyframes nikola-glyph-spin": {
              "0%": { transform: "rotate(0deg)" },
              "100%": { transform: "rotate(360deg)" },
            },
            "@keyframes nikola-glyph-scan": {
              "0%, 100%": { transform: "translateX(-3px)" },
              "50%": { transform: "translateX(3px)" },
            },
            "@keyframes nikola-glyph-sway": {
              "0%, 100%": { transform: "rotate(-12deg)" },
              "50%": { transform: "rotate(12deg)" },
            },
            "@keyframes nikola-glyph-rise": {
              "0%, 100%": { transform: "translateY(2px)" },
              "50%": { transform: "translateY(-3px)" },
            },
            "@media (prefers-reduced-motion: reduce)": { animation: "none" },
          }}
        >
          {failed ? "×" : icon.glyph}
        </Box>
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1 }}>
          <Typography
            sx={{
              color: failed ? "error.main" : "text.primary",
              fontSize: ".68rem",
              fontWeight: 900,
              letterSpacing: ".065em",
            }}
          >
            {failed ? "EVIDENCE CHECK COULD NOT FINISH" : runningLabel}
          </Typography>
          {progress != null && !failed && (
            <Typography sx={{ color: "primary.main", fontSize: ".66rem", fontWeight: 950 }}>{progress}%</Typography>
          )}
        </Box>
        {!complete && (
          <Typography sx={{ mt: 0.3, color: "text.secondary", fontSize: ".68rem" }}>
            {failed ? "Nikola will answer only from evidence that was available." : runningDetail}
          </Typography>
        )}
        {complete && safeActivity.detail && (
          <Typography sx={{ mt: 0.1, color: "text.secondary", fontSize: ".62rem" }}>{safeActivity.detail}</Typography>
        )}
        {safeActivity.status === "running" && (safeActivity.kind === "analysis_started" || progress != null) && (
          <LinearProgress
            aria-label="Nikola analysis in progress"
            variant={progress == null ? "indeterminate" : "determinate"}
            value={progress ?? undefined}
            sx={{
              mt: 1,
              height: 3,
              bgcolor: "rgba(240,236,226,.12)",
              "& .MuiLinearProgress-bar": { bgcolor: "primary.main" },
            }}
          />
        )}
      </Box>
    </Box>
  );
};
